import { calculators, type entities, enums, formatters, productMatching, selectors } from "@fraction/shared";
import _ from "lodash";
import { type DisplayedRate, selectOfferDisplayedRate } from "./statedRate";

/**
 * Offer-metric derivation shared by the web offer cards / comparison table and
 * the mobile "Product offers" tab. Reuses the shared single-source-of-truth
 * calculators (`productMatching.getEffectivePricing`,
 * `calculators.product.estimatedMonthlyPayment`,
 * `calculators.rates.getLoanGrowthFactor`) so both surfaces quote the same
 * numbers. Calculator inputs are narrowed to the exact fields they read so this
 * compiles under the mobile app's `exactOptionalPropertyTypes`.
 */

export interface OfferPremium {
  label: string;
  bps: number;
}

/** Display-ready, derived metrics for a single evaluation. */
export interface OfferMetrics {
  /** Effective annual rate as a decimal (e.g. 0.0924), or undefined. Sort/compare on this. */
  rateDecimal: number | undefined;
  /** The rate to PRINT — stated for the deal's `statedRateBasis` (see `selectOfferDisplayedRate`). */
  displayedRate: DisplayedRate | undefined;
  /** Estimated monthly payment in cents, or undefined. */
  monthlyPaymentCents: number | undefined;
  /** Max approved loan amount in cents (LTV × property value, capped), or undefined. */
  maxLoanCents: number | undefined;
  /** Effective max LTV at funding/entry as a decimal (e.g. 0.65), or undefined. */
  maxLTVDecimal: number | undefined;
  /** Whether the stored policy cap had to be converted from an end-of-term LTV. */
  ltvIsEndOfTerm: boolean;
  termLengthMonths: number | undefined;
  termLabel: string | undefined;
  paymentTypeLabel: string | undefined;
  /** Combined lender-fee label (percentage and/or flat dollar), or undefined. */
  feeLabel: string | undefined;
  /** Total fee in bps, used for best-value comparison. */
  totalFeeBps: number | undefined;
  amortizationLabel: string;
  /** Acceptable income-document labels. */
  docs: string[];
  premiums: OfferPremium[];
  /**
   * Projected post-funding TDS/GDS this evaluation qualified at (decimals,
   * e.g. 0.41 — post debt-payoff, with the product's income-type exclusions).
   * Undefined when the application had no qualifying income for this product.
   */
  postFundingTds: number | undefined;
  postFundingGds: number | undefined;
  /** Prepayment privileges + penalty, e.g. "20%/20% · 2% yr 1 then 1%". */
  prepaymentLabel: string | undefined;
  /** How the borrower qualifies, e.g. "Qualifies at contract + 2.00% (floor 5.25%)". */
  qualifyingRateLabel: string | undefined;
  /** Rate-hold period in days (product.daysRateLockedFor). */
  rateHoldDays: number | undefined;
  /**
   * The credit score this rate requires. Also the floor on whose income counts:
   * an applicant below it can be on the mortgage, but their income is left out of
   * the TDS/GDS denominator (see `productMatching.selectFicoIncomeExclusions`).
   */
  minimumFico: number | undefined;
  /** True when every applicant must clear the floor, not just the pricing applicant. */
  minimumFicoAppliesToAllApplicants: boolean;
  /** Display form, e.g. "660" or "660 (all applicants)". */
  minimumFicoLabel: string | undefined;
}

const PRODUCT_TYPE_MAP = formatters.enums.PRODUCT_TYPE_MAP as Record<string, string>;

function buildTermLabel(rate?: entities.RateT): string | undefined {
  if (rate?.termLengthMonths === undefined) {
    return undefined;
  }
  const openAfter =
    rate.openTermAfterMonths || rate.product?.minOpenTermAfterMonths || rate.product?.maxOpenTermAfterMonths;
  const inner =
    rate.openTermAfterMonths === 0
      ? "open"
      : rate.product?.earlyPayoutType === "closed"
        ? "closed"
        : openAfter
          ? `open after ${openAfter} months`
          : "closed";
  return `${rate.termLengthMonths} months (${inner})`;
}

function buildFeeLabel(pricing: productMatching.EffectivePricing): string | undefined {
  const parts: string[] = [];
  if (pricing.totalFeeBps != null) {
    parts.push(
      formatters.number.getPercentageFromDecimal(formatters.number.convertBpsToDecimal(pricing.totalFeeBps))
    );
  }
  if (pricing.lenderFeeCents != null) {
    parts.push(formatters.number.getCurrencyFromNumber(pricing.lenderFeeCents));
  }
  return parts.length ? parts.join(" + ") : undefined;
}

function buildAmortizationLabel(product?: entities.ProductT): string {
  if (product?.type === enums.ProductType.AMORTIZING_MORTGAGE && product?.maxAmortizationMonths) {
    const min = product.minAmortizationMonths ? `${product.minAmortizationMonths / 12} to ` : "";
    return `${min}${product.maxAmortizationMonths / 12} years`;
  }
  return "Not amortizing";
}

/** The evaluation's premiums with readable labels, at face value in bps ("Condominium", 25). */
export function selectOfferPremiums(evaluation: entities.ProductRateEvaluationT): OfferPremium[] {
  return (evaluation.rateAdjustments || []).map((adjustment) => {
    const details = adjustment.evaluationDetails as { reason?: string; fieldName?: string } | undefined;
    const label = details?.fieldName
      ? _.startCase(
          details.fieldName
            .replace(/^(product|rate)\./, "")
            .replace(/PremiumBps$/, "")
            .replace(/InterestRatePremium$/, "Regional")
        )
      : details?.reason || "Adjustment";
    return { label, bps: adjustment.amount || 0 };
  });
}

function buildPrepaymentLabel(product?: entities.ProductT): string | undefined {
  const parts: string[] = [];
  if (product?.prepaymentPrivilegePercent != null) {
    const privilege = formatters.number.getPercentageFromDecimal(Number(product.prepaymentPrivilegePercent), {
      digits: 0,
    });
    const increase =
      product.paymentIncreasePrivilegePercent != null
        ? `/${formatters.number.getPercentageFromDecimal(Number(product.paymentIncreasePrivilegePercent), {
            digits: 0,
          })}`
        : "";
    parts.push(`${privilege}${increase} privileges`);
  }
  if (product?.prepaymentPenaltyDescription) {
    parts.push(product.prepaymentPenaltyDescription);
  }
  return parts.length ? parts.join(" · ") : undefined;
}

function buildQualifyingRateLabel(product?: entities.ProductT): string | undefined {
  if (!product?.qualifyingRateType || product.qualifyingRateType === enums.QualifyingRateType.CONTRACT) {
    return undefined;
  }
  const buffer =
    product.qualifyingRateBufferBps != null
      ? `contract + ${formatters.number.getPercentageFromDecimal(
          formatters.number.convertBpsToDecimal(product.qualifyingRateBufferBps)
        )}`
      : "contract";
  if (product.qualifyingRateType === enums.QualifyingRateType.CONTRACT_PLUS_BUFFER) {
    return `Qualifies at ${buffer}`;
  }
  const floor =
    product.qualifyingRateFloor != null
      ? ` (floor ${formatters.number.getPercentageFromDecimal(Number(product.qualifyingRateFloor))})`
      : "";
  return `Qualifies at greater of benchmark${floor} or ${buffer}`;
}

/** The deal context an offer's metrics are derived against — see {@link selectOfferContext}. */
export interface OfferContext {
  loanAmount?: number | undefined;
  propertyValue?: number | undefined;
  /** The deal's basis decides which rate is printed; missing reads as EFFECTIVE_ANNUAL. */
  statedRateBasis?: enums.StatedRateBasis | null | undefined;
}

/**
 * Derive every display value the banner / comparison table / offer cards need
 * for a single evaluation.
 */
export function deriveOfferMetrics(
  evaluation: entities.ProductRateEvaluationT,
  { loanAmount, propertyValue, statedRateBasis }: OfferContext
): OfferMetrics {
  const rate = evaluation.rate;
  const product = rate?.product;
  const pricing = productMatching.getEffectivePricing(evaluation);

  const monthlyPaymentCents = calculators.product.estimatedMonthlyPayment({
    // Narrow to just the fields the calculator reads so each property stays
    // present (required under the mobile app's exactOptionalPropertyTypes).
    product: product
      ? {
          type: product.type,
          maxAmortizationMonths: product.maxAmortizationMonths,
          minAmortizationMonths: product.minAmortizationMonths,
          compoundFrequency: product.compoundFrequency,
          lenderId: product.lenderId,
          lender: product.lender,
        }
      : undefined,
    rate: rate ? { minimumRate: rate.minimumRate } : undefined,
    loanAmount,
    annualRate: pricing.rateDecimal,
  });

  const ltvIsEndOfTerm = productMatching.enforcesMaxLtvAtEndOfTerm(product);
  // Prefers the premium-adjusted effective rate over the tier's headline rate: that is what the
  // balance will actually accrue at, so it is what the cap has to be discounted by.
  const maxLtvAtFunding = productMatching.getMaxLtvAtFunding({
    product,
    maxLtv: pricing.maxLTVDecimal,
    annualRate: pricing.rateDecimal ?? (rate?.minimumRate != null ? Number(rate.minimumRate) : undefined),
    termLengthMonths: rate?.termLengthMonths ?? undefined,
  });

  const maxByLtv =
    propertyValue && maxLtvAtFunding != null ? Math.round(maxLtvAtFunding * propertyValue) : undefined;
  const maxByProduct = product?.maximumLoanAmount;
  const maxLoanCents =
    (maxByLtv && maxByProduct ? Math.min(maxByLtv, maxByProduct) : maxByLtv || maxByProduct) ?? undefined;

  const docKeys =
    rate?.acceptableIncomeDocumentConfirmationTypes ||
    product?.acceptableIncomeDocumentConfirmationTypes ||
    [];

  // The headline number is the tier's own `minimumFico`; `minimumAllApplicantsFico`
  // (or `requiresAllApplicantsMeetFico`) only changes who has to clear it. Shown as
  // one figure so the column reads as a single threshold.
  const requiresAllApplicants =
    rate?.minimumAllApplicantsFico != null || !!product?.requiresAllApplicantsMeetFico;
  const minimumFico = rate?.minimumFico ?? rate?.minimumAllApplicantsFico ?? undefined;

  return {
    rateDecimal: pricing.rateDecimal,
    displayedRate: selectOfferDisplayedRate(pricing, statedRateBasis),
    monthlyPaymentCents,
    maxLoanCents,
    maxLTVDecimal: maxLtvAtFunding ?? undefined,
    ltvIsEndOfTerm,
    termLengthMonths: rate?.termLengthMonths ?? undefined,
    termLabel: buildTermLabel(rate),
    paymentTypeLabel: product?.type ? PRODUCT_TYPE_MAP[product.type] || _.startCase(product.type) : undefined,
    feeLabel: buildFeeLabel(pricing),
    totalFeeBps: pricing.totalFeeBps,
    amortizationLabel: buildAmortizationLabel(product),
    docs: docKeys.map((doc) => productMatching.INCOME_DOCUMENT_TYPE_LABELS[doc] || _.startCase(doc)),
    premiums: selectOfferPremiums(evaluation),
    postFundingTds: evaluation.postFundingTds != null ? Number(evaluation.postFundingTds) : undefined,
    postFundingGds: evaluation.postFundingGds != null ? Number(evaluation.postFundingGds) : undefined,
    prepaymentLabel: buildPrepaymentLabel(product),
    qualifyingRateLabel: buildQualifyingRateLabel(product),
    rateHoldDays: product?.daysRateLockedFor ?? undefined,
    minimumFico,
    minimumFicoAppliesToAllApplicants: requiresAllApplicants,
    minimumFicoLabel:
      minimumFico != null ? `${minimumFico}${requiresAllApplicants ? " (all applicants)" : ""}` : undefined,
  };
}

/**
 * Loan amount + property value (cents) used to derive per-offer payments / max loan, and the deal's
 * stated-rate basis that decides which rate the offers print.
 */
export function selectOfferContext(deal: entities.ApplicationT): {
  loanAmount: number | undefined;
  propertyValue: number | undefined;
  statedRateBasis: enums.StatedRateBasis | undefined;
} {
  const loanAmount = selectors.application.selectApplicationAmount(deal);
  // Combined pledged security first — on a blanket the offer maths has to see
  // everything the charge attaches to. `latestValuationCents` is a per-property
  // SQL column and stays the fallback for lite rows, whose valuations aren't
  // loaded for the selector to walk.
  const propertyValue =
    selectors.application.selectLendingValueCents(deal) || deal.property?.latestValuationCents;
  return { loanAmount, propertyValue, statedRateBasis: deal.statedRateBasis };
}
